var __assign = this && this.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];
      for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
    }
    return t;
  };
  return __assign.apply(this, arguments);
};
var _a, _b, _c;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Box, Stack, Typography } from '@mui/material';
import { useContext } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { SubTitle } from '../layout/styled';
import { OrgConfigStep } from './constants';
import { OrgConfigWizardContext } from './context';
var STEP_DESCRIPTIONS = (_a = {}, _a[OrgConfigStep.Locations] = {
  i18nKey: 'orgConfigWizard.locationsStep.addLocationDescription',
  defaultValue: 'Enter unique site locations for your organization with associated number of seats.'
}, _a[OrgConfigStep.Hours] = {
  i18nKey: 'orgConfigWizard.hoursStep.description',
  defaultValue: 'Enter the hours of operation for each site location. Specify the time zone at that location and add any holidays that should be considered during the planning process for that location.'
}, _a[OrgConfigStep.StaffSettings] = {
  i18nKey: 'orgConfigWizard.staffSettingsStep.description',
  defaultValue: 'Enter staff settings for attrition, recruitment and hours of work.'
}, _a[OrgConfigStep.Shrinkage] = {
  i18nKey: 'orgConfigWizard.shrinkageStep.description',
  defaultValue: 'Add all shrinkage factors that apply to your organization and specify whether they are in or out of office factors, as well as planned/unplanned.'
}, _a[OrgConfigStep.SupportRoles] = {
  i18nKey: 'orgConfigWizard.supportRolesStep.description',
  defaultValue: 'Add all applicable support roles required across your organization. The ratio field is optional, and can be overridden within the planning process.'
}, _a[OrgConfigStep.Financials] = {
  i18nKey: 'orgConfigWizard.financialsStep.description',
  defaultValue: 'Add the salaries/wages for both agent and support staff roles. Indicate the currency applicable for each site location.'
}, _a[OrgConfigStep.ClientsLobs] = {
  i18nKey: 'orgConfigWizard.clientsLobsStep.description',
  defaultValue: "Define the lines of business and attributes applicable to your organization. If you import groups via integration, don't forget to add your lines of business for each imported group."
}, _a[OrgConfigStep.Seats] = {
  i18nKey: 'orgConfigWizard.seatsStep.description',
  defaultValue: 'Please reserve the required seats at each location. The reserved seats indicate the total peak number of seats required on a monthly or weekly basis.'
}, _a[OrgConfigStep.Review] = {
  i18nKey: 'orgConfigWizard.reviewStep.description',
  defaultValue: 'Select each location and review your information to adjust any data. Select "FINISH" to complete your organization configuration.'
}, _a);
var STEP_TITLE = (_b = {}, _b[OrgConfigStep.Locations] = {
  i18nKey: 'orgConfigWizard.locationsStep.title'
}, _b[OrgConfigStep.Hours] = {
  i18nKey: 'orgConfigWizard.hoursStep.title'
}, _b[OrgConfigStep.StaffSettings] = {
  i18nKey: 'orgConfigWizard.staffSettingsStep.title'
}, _b[OrgConfigStep.Shrinkage] = {
  i18nKey: 'orgConfigWizard.shrinkageStep.title'
}, _b[OrgConfigStep.SupportRoles] = {
  i18nKey: 'orgConfigWizard.supportRolesStep.title'
}, _b[OrgConfigStep.Financials] = {
  i18nKey: 'orgConfigWizard.financialsStep.title'
}, _b[OrgConfigStep.ClientsLobs] = {
  i18nKey: 'orgConfigWizard.clientsLobsStep.title'
}, _b[OrgConfigStep.Seats] = {
  i18nKey: 'orgConfigWizard.seatsStep.title'
}, _b[OrgConfigStep.Review] = {
  i18nKey: 'orgConfigWizard.reviewStep.title'
}, _b);
var HEADER_TOP_RIGHT_SLOT_ID = (_c = {}, _c[OrgConfigStep.Financials] = 'org-config-financials-header-top-right', _c[OrgConfigStep.ClientsLobs] = 'org-config-clients-lobs-header-top-right', _c[OrgConfigStep.Hours] = 'org-config-hours-header-top-right', _c);
export var OrgConfigDescription = function (_a) {
  var _b;
  var _c = _a === void 0 ? {} : _a,
    titleSlot = _c.titleSlot;
  var t = useTranslation('orgConfig').t;
  var _d = useContext(OrgConfigWizardContext),
    step = _d.step,
    stepIndex = _d.stepIndex;
  var title = STEP_TITLE[step];
  var desc = STEP_DESCRIPTIONS[step];
  return _jsxs(_Fragment, {
    children: [_jsxs(Stack, __assign({
      direction: "row",
      justifyContent: "space-between",
      alignItems: "flex-start"
    }, {
      children: [_jsx(Typography, __assign({
        variant: "h1"
      }, {
        children: t('orgConfigWizard.stepTitle', {
          index: stepIndex + 1,
          name: t(title.i18nKey)
        })
      })), titleSlot]
    })), desc && (step === OrgConfigStep.Financials || step === OrgConfigStep.ClientsLobs || step === OrgConfigStep.Hours ? _jsxs(Box, __assign({
      sx: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 4,
        flexWrap: 'wrap',
        mb: 3
      }
    }, {
      children: [_jsx(SubTitle, __assign({
        sx: {
          flex: '1 1 320px',
          mb: 0,
          maxWidth: {
            md: '58%'
          }
        }
      }, {
        children: _jsx(Trans, {
          t: t,
          i18nKey: desc.i18nKey,
          components: {
            b: _jsx("strong", {})
          },
          defaultValue: desc.defaultValue
        })
      })), _jsx(Box, {
        id: (_b = HEADER_TOP_RIGHT_SLOT_ID[step]) !== null && _b !== void 0 ? _b : 'org-config-header-top-right',
        sx: {
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          flexShrink: 0,
          ml: 'auto'
        }
      })]
    })) : _jsx(SubTitle, __assign({
      sx: {
        mb: 3
      }
    }, {
      children: _jsx(Trans, {
        t: t,
        i18nKey: desc.i18nKey,
        components: {
          b: _jsx("strong", {})
        },
        defaultValue: desc.defaultValue
      })
    })))]
  });
};